import { z } from 'zod';
import { isCalendarStartUrl } from './calendar-sign-in';
import { apiClient, getApiOrigin } from '.';
/**
 * Calendar reminders for "Interpret my meeting" (docs/external-meeting-vrs.md,
 * phase 2): the client's reminders, and the calendar connections that feed
 * them. Reminders carry no meeting link; requesting an interpreter names the
 * reminder and the server supplies the link.
 */
const platform = z.enum(['zoom', 'meet', 'teams', 'webex']);
export const meetingReminderSchema = z.object({
    endsAt: z.string(),
    host: z.string(),
    id: z.string().uuid(),
    platform,
    startsAt: z.string(),
    title: z.string().nullable().optional()
});
const connectionSchema = z.object({
    accountEmail: z.string().nullable().optional(),
    provider: z.enum(['google', 'microsoft']),
    remindersEnabled: z.boolean().optional(),
    status: z.string(),
    syncError: z.string().nullable().optional()
});
const TIMEOUT_MS = 15000;
function bounded() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    return { done: () => clearTimeout(timer), signal: controller.signal };
}
/** Parsed reminders, or null when they could not be confirmed. */
export async function fetchMeetingReminders() {
    const { done, signal } = bounded();
    try {
        const response = await apiClient.get('/api/client/meeting-reminders', { signal });
        const parsed = z.object({ reminders: z.array(z.unknown()) }).safeParse(response.data);
        if (response.error || !parsed.success)
            return null;
        return parsed.data.reminders.flatMap(item => {
            const reminder = meetingReminderSchema.safeParse(item);
            return reminder.success ? [reminder.data] : [];
        });
    }
    catch {
        return null;
    }
    finally {
        done();
    }
}
export async function dismissMeetingReminder(id) {
    const { done, signal } = bounded();
    try {
        const response = await apiClient.post(`/api/client/meeting-reminders/${encodeURIComponent(id)}/dismiss`, {}, { signal });
        return !response.error;
    }
    catch {
        return false;
    }
    finally {
        done();
    }
}
export async function fetchCalendarConnections() {
    const { done, signal } = bounded();
    try {
        const response = await apiClient.get('/api/client/vri/calendar/connections', { signal });
        const parsed = z.object({ connections: z.array(z.unknown()) }).safeParse(response.data);
        if (response.error || !parsed.success)
            return null;
        return parsed.data.connections.flatMap(item => {
            const connection = connectionSchema.safeParse(item);
            return connection.success ? [connection.data] : [];
        });
    }
    catch {
        return null;
    }
    finally {
        done();
    }
}
/** The Malka /start link that begins calendar sign-in, or null. Never a provider URL. */
export async function calendarSignInUrl(provider) {
    const { done, signal } = bounded();
    try {
        const response = await apiClient.get(`/api/client/vri/calendar/auth-url?provider=${provider}`, { signal });
        const url = z.object({ authorizationUrl: z.string() }).safeParse(response.data);
        return url.success && isCalendarStartUrl(url.data.authorizationUrl, getApiOrigin()) ? url.data.authorizationUrl : null;
    }
    catch {
        return null;
    }
    finally {
        done();
    }
}
export async function setCalendarReminders(provider, enabled) {
    const { done, signal } = bounded();
    try {
        const response = await apiClient.put(`/api/client/vri/calendar/connections/${provider}/reminders`, { enabled }, { signal });
        return !response.error;
    }
    catch {
        return false;
    }
    finally {
        done();
    }
}
export async function disconnectCalendar(provider) {
    const { done, signal } = bounded();
    try {
        const response = await apiClient.del(`/api/client/vri/calendar/connections/${provider}`, { signal });
        return !response.error;
    }
    catch {
        return false;
    }
    finally {
        done();
    }
}
